import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },

      // Validate token on app load
      validateSession: async () => {
        const token = get().token;
        if (!token) {
          set({ isAuthenticated: false });
          return false;
        }
        try {
          const res = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          // Parse JSON once
          let user = null;
          try {
            user = await res.json();
          } catch (e) {
            // If it's not JSON, it's definitely not a valid session
          }

          if (!res.ok) {
            // Only clear token if it's actually invalid (401/403)
            if (res.status === 401 || res.status === 403) {
              set({ user: null, token: null, isAuthenticated: false });
            }
            return false;
          }

          if (!user) return false;

          set({ user, isAuthenticated: true });
          return true;
        } catch (error) {
          // On network errors, don't clear the token - just fail validation
          // This prevents logout on temporary network issues
          console.error('Session validation failed:', error);
          return false;
        }
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${API}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.detail || 'Login failed');

          set({ user: data.user, token: data.access_token, isAuthenticated: true, isLoading: false });
          return data;
        } catch (err) {
          set({ error: err.message, isLoading: false });
          throw err;
        }
      },

      register: async (name, email, password) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${API}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, email, password }),
          });

          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.detail || 'Registration failed');

          set({ user: data.user, token: data.access_token, isAuthenticated: true, isLoading: false });
          return data;
        } catch (err) {
          set({ error: err.message, isLoading: false });
          throw err;
        }
      },

      logout: () => {
        set({ user: null, token: null, isAuthenticated: false, error: null });
        // Clear localStorage
        localStorage.removeItem('tracker-auth');
        localStorage.removeItem('tracker_active_timer');
      },

      getHeaders: () => {
        const token = get().token;
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    }),
    {
      name: 'tracker-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
